import { type FieldValidatorType } from "@progress/kendo-vue-form";
import { type ComposerTranslation } from 'vue-i18n'
import { GetPasswordRegex } from '../stores/SettingsStore'
import type { IPasswordPolicy } from '../types/CommonTypes'

// Cached regex pattern to avoid calling the async function repeatedly
const cachedPasswordPattern: { regex: string | null } = { regex: null };

export function createRequiredFieldValidator(t: ComposerTranslation,
  fieldName: string,
): FieldValidatorType {
  return (value: string) => {
    if (!value) {
      return t("validators.requiredField", {fieldName: t("columns." + fieldName)});
    }
    return "";
  };
}

export function createFieldLengthValidator(t: ComposerTranslation,
  fieldName: string,
  maximumLength: number,
): FieldValidatorType {
  return (value: string) => {
    if (value && value.length > maximumLength) {
      return t("validators.fieldLength", {fieldName: t("columns." + fieldName), maximumLength});
    }
    return "";
  };
}

export function createEmailFieldValidator(t: ComposerTranslation,
  fieldName: string,
): FieldValidatorType {
  return (value: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!emailRegex.test(value)) {
      return t("validators.emailField", {fieldName: t("columns." + fieldName)});
    }
    return "";
  };
}

export function createNameFieldValidator(t: ComposerTranslation,
  fieldName: string,
): FieldValidatorType {
  return (value: string) => {
    if (!value) return "";
    
    // Allow only letters, spaces, hyphens, and apostrophes
    // This regex allows Unicode letters to support international names including EU accented letters
    const nameRegex = /^[\p{L}\s'-]+$/u;
    
    if (!nameRegex.test(value)) {
      return t("validators.nameField", {fieldName: t("columns." + fieldName)});
    }
    if (value.trim().length < 2) {
      return t("validators.fieldMinLength", {fieldName: t("columns." + fieldName), minimumLength: 2});
    }
    if (value.length > 50) {
      return t("validators.fieldLength", {fieldName: t("columns." + fieldName), maximumLength: 50});
    }
    return "";
  };
}

export function createPhoneFieldValidator(t: ComposerTranslation,
  fieldName: string,
  _countryCode?: string,
): FieldValidatorType {
  return (value: string) => {
    if (!value) return "";

    // CRITICAL: Phone numbers must be NUMERIC ONLY for JAS shipping system compatibility
    // No spaces, no symbols (+, -, (, ), etc.) - only digits 0-9
    const numericOnlyRegex = /^[0-9]+$/;
    
    if (!numericOnlyRegex.test(value)) {
      return t("validators.phoneNumericOnly", {fieldName: t("columns." + fieldName)}) ||
             t("validators.formatField", {fieldName: t("columns." + fieldName)}) ||
             `${t("columns." + fieldName)} must contain only numbers (no spaces or symbols)`;
    }

    // Universal length validation: 3-15 digits for all countries
    const digitCount = value.length;

    if (digitCount < 3 || digitCount > 15) {
      return t("validators.phoneLengthInvalid", {
        fieldName: t("columns." + fieldName),
        min: 3,
        max: 15
      }) ||
      `${t("columns." + fieldName)} must be 3-15 digits`;
    }

    return "";
  };
}

/**
 * Preloads password regex pattern from settings
 * Call this before validation is needed
 */
export async function preloadPasswordRegex(): Promise<void> {
  try {
    if (!cachedPasswordPattern.regex) {
      cachedPasswordPattern.regex = await GetPasswordRegex();
      console.log("Password regex pattern loaded");
    }
  } catch (error) {
    console.error("Error loading password regex pattern:", error);
  }
}

/**
 * Extracts the allowed special characters and minimum length from a whitelist-style password regex
 * (e.g. ...[A-Za-z\d@$!%*?&]{8,}$). Returns null when the regex is not in that form.
 */
export function parsePasswordPolicy(regexStr: string): IPasswordPolicy | null {
  const match = regexStr.match(/\[A-Za-z\\d([^\]]*)\]\{(\d+),\}/)
  if (!match) return null

  // Unescape regex escapes (e.g. "\-") so specialChars holds the literal characters
  return { specialChars: match[1].replace(/\\(.)/g, '$1'), minLength: parseInt(match[2], 10) }
}

export function escapeForCharClass(chars: string): string {
  return chars.replace(/[\\\]^-]/g, '\\$&')
}

/**
 * Returns the "only use these special characters" message when the password contains a character the
 * password regex does not allow, otherwise an empty string.
 * @param t - Translation function
 * @param regexStr - Password regex from GetPasswordRegex
 * @param value - Password to check
 */
export function getPasswordProhibitedCharMessage(t: ComposerTranslation, regexStr: string, value: string): string {
  const passwordPolicy = value && regexStr ? parsePasswordPolicy(regexStr) : null
  if (!passwordPolicy) return ""

  const prohibitedCharRegex = new RegExp('[^A-Za-z0-9' + escapeForCharClass(passwordPolicy.specialChars) + ']')
  if (prohibitedCharRegex.test(value)) {
    return t("validators.passwordProhibitedChar", { allowedChars: passwordPolicy.specialChars })
  }
  return ""
}

/**
 * Creates a validator that checks if the password matches the required format defined in SettingsStore
 * @param t - Translation function
 * @param fieldName - Name of the field being validated
 * @returns A field validator function
 */
export function createPasswordFormatValidator(t: ComposerTranslation, fieldName: string): FieldValidatorType {
  // Attempt to load regex pattern if it's not already loaded
  if (!cachedPasswordPattern.regex) {
    preloadPasswordRegex().catch(error => {
      console.error("Failed to preload password regex:", error);
    });
  }
  
  return (value: string) => {
    if (!value) return "";
    
    // Skip validation if pattern isn't loaded yet
    if (!cachedPasswordPattern.regex) return "";
    
    try {
      const prohibitedCharMessage = getPasswordProhibitedCharMessage(t, cachedPasswordPattern.regex, value);
      if (prohibitedCharMessage) {
        return prohibitedCharMessage;
      }

      // Create RegExp object from the string pattern
      const passwordRegex = new RegExp(cachedPasswordPattern.regex);

      if (!passwordRegex.test(value)) {
        return t("validators.formatField", { fieldName: t("columns." + fieldName) });
      }
      
      return "";
    } catch (error) {
      console.error("Error validating password format:", error);
      return "";
    }
  };
}
